/**
 * Hand-written mirror of `docs/api-contract.md`.
 *
 * Nothing here is invented: every field below appears in the contract, and no
 * field in the contract is missing. If the contract changes, change this file
 * first and let the type errors lead you to the call sites.
 *
 * All JSON is camelCase. All dates are ISO-8601 strings.
 */

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

export type ApiErrorBody = {
  detail: string;
  code?: string;
};

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

export type MeStats = {
  entries: number;
  collections: number;
  domains: number;
  quarryFound: number;
};

export type Me = {
  id: string;
  email: string;
  name: string | null;
  givenName: string | null;
  displayName: string;
  avatarUrl: string | null;
  createdAt: string;
  /** Used for new finds whose photo carries no location of its own. */
  defaultPlace: DefaultPlace | null;
  stats: MeStats;
};

export type DefaultPlace = {
  label: string | null;
  country: string | null;
  countryCode: string | null;
  admin1: string | null;
  locality: string | null;
  lat: number | null;
  lon: number | null;
};

/** PATCH /api/me — sets or clears the account's default place. */
export type MePatch = {
  placeQuery: string;
};

/* -------------------------------------------------------------------------- */
/* Upload flow                                                                */
/* -------------------------------------------------------------------------- */

export type PresignRequest = {
  filename: string;
  contentType: string;
  byteSize: number;
};

export type PresignResponse = {
  entryId: string;
  storageKey: string;
  uploadUrl: string;
  method: "PUT";
  /** Send these verbatim on the PUT. */
  headers: Record<string, string>;
  /** Seconds. */
  expiresIn: number;
};

/** The only content types the API will presign. */
export const ACCEPTED_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

export type AcceptedContentType = (typeof ACCEPTED_CONTENT_TYPES)[number];

/** 25 MB; the API answers 413 beyond this. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/* -------------------------------------------------------------------------- */
/* Entries                                                                    */
/* -------------------------------------------------------------------------- */

export type EntryStatus =
  | "pending_upload"
  | "queued"
  | "developing"
  | "complete"
  | "rejected"
  | "failed";

export type PlaceSource = "exif" | "manual" | "default" | "none";

export type Place = {
  /** "Madison, Wisconsin" */
  label: string | null;
  country: string | null;
  /** ISO-3166-1 alpha-2 */
  countryCode: string | null;
  /** State / province */
  admin1: string | null;
  locality: string | null;
  lat: number | null;
  lon: number | null;
  source: PlaceSource;
};

export type EntryCollectionRef = {
  id: string;
  title: string;
  subtitle: string | null;
  isPrimary: boolean;
};

export type Entry = {
  id: string;
  status: EntryStatus;
  error: string | null;
  /** Set when status === "rejected". */
  rejectionReason: string | null;

  /** Presigned GET, roughly one hour of life. */
  imageUrl: string | null;
  thumbUrl: string | null;
  contentType: string;
  width: number | null;
  height: number | null;

  /** From EXIF. */
  capturedAt: string | null;
  /** The date the scrapbook stamps. */
  foundAt: string;
  createdAt: string;
  classifiedAt: string | null;

  place: Place;

  /** Registry domain key, e.g. "flora". */
  domain: string | null;
  /** Registry rank key. */
  rank: string | null;
  /** Scrapbook template: herbarium | plate | specimen | elevation | label | card. */
  variant: string;
  /** Render as a caution strip when present. */
  safetyNote: string | null;

  commonName: string | null;
  scientificName: string | null;
  title: string | null;
  /** Already falls back through userTitle -> title -> commonName. */
  displayTitle: string;
  /** 0..1 */
  confidence: number | null;
  /** 2-4 sentence field note. */
  summary: string | null;
  identificationNotes: string | null;
  /** Keys, labels and enum spellings are described by /api/manifest. */
  attributes: Record<string, unknown>;
  tags: string[];
  /** 1..5 */
  rarity: number | null;

  userTitle: string | null;
  notes: string | null;
  favourite: boolean;

  collections: EntryCollectionRef[];
};

export type EntryPage = {
  items: Entry[];
  nextCursor: string | null;
};

export type EntryStatusRow = {
  id: string;
  status: EntryStatus;
  updatedAt: string;
};

/** PATCH /api/entries/{id} — all fields optional. */
export type EntryPatch = {
  userTitle?: string | null;
  notes?: string | null;
  favourite?: boolean;
  /**
   * "Madison, Wisconsin" — geocoded, then the entry is re-filed.
   * "" clears the place. The response may take about a second.
   */
  placeQuery?: string | null;
};

/** Query parameters for GET /api/entries. All optional. */
export type EntryFilters = {
  /** 1–100, default 24. */
  limit?: number;
  cursor?: string;
  /** Registry domain key, e.g. "flora". */
  domain?: string;
  collectionId?: string;
  favourite?: boolean;
  /** Only entries with no place, and so no collection. */
  unfiled?: boolean;
  /** Substring match on names, summary, notes and tags. */
  q?: string;
};

/* -------------------------------------------------------------------------- */
/* Collections                                                                */
/* -------------------------------------------------------------------------- */

export type GeoScope = "locality" | "admin1" | "country";

export type QuarryState = "pending" | "ready" | "failed";

export type Collection = {
  id: string;
  /** "flora:tree:admin1:US-WI" */
  key: string;
  /** "Arboreal Wisconsin" */
  title: string;
  subtitle: string | null;
  domain: string;
  rank: string;
  /** "Tree" */
  rankLabel: string;
  /** "trees" */
  rankPlural: string;
  variant: string;
  geoScope: GeoScope;
  /** "US-WI" */
  geoCode: string;
  /** "Wisconsin" */
  geoLabel: string;
  quarryState: QuarryState;
  /** Suggested targets, excluding bonuses. */
  quarryCount: number;
  /** Suggested targets ticked off. */
  foundCount: number;
  /** Finds that were not on the list. */
  bonusCount: number;
  /** 0..1 over suggested quarry only. */
  progress: number;
  coverImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type QuarryFoundEntry = {
  id: string;
  thumbUrl: string | null;
  displayTitle: string;
};

export type Quarry = {
  id: string;
  name: string;
  scientificName: string | null;
  blurb: string | null;
  whereToLook: string | null;
  seasonality: string | null;
  /** 1..5 */
  rarity: number;
  isFound: boolean;
  /** Found but never suggested. */
  isBonus: boolean;
  foundAt: string | null;
  foundEntry: QuarryFoundEntry | null;
};

export type CollectionDetail = Collection & { quarry: Quarry[] };

/* -------------------------------------------------------------------------- */
/* Manifest                                                                   */
/* -------------------------------------------------------------------------- */

export type ManifestAttrKind =
  | "string"
  | "text"
  | "integer"
  | "number"
  | "boolean"
  | "enum"
  | "string_list";

export type ManifestAttr = {
  key: string;
  /** "Leaf type" — never hardcode this in the UI. */
  label: string;
  kind: ManifestAttrKind;
  /** Enum values, snake_case. */
  choices: string[];
  /** Show even when empty, as a blank ruled line. */
  prominent: boolean;
  unit: string | null;
};

export type ManifestRank = {
  key: string;
  label: string;
  plural: string;
  titleTemplate: string;
  subtitle: string;
  geoScope: string;
};

export type ManifestDomain = {
  /** "flora" */
  key: string;
  /** "Flora" */
  label: string;
  variant: string;
  geoScope: string;
  safetyNote: string | null;
  /** The one attribute key (if any) worth showing as an at-a-glance third fact. */
  headlineAttr: string | null;
  ranks: ManifestRank[];
  attrs: ManifestAttr[];
};

export type Manifest = {
  domains: ManifestDomain[];
};

/* -------------------------------------------------------------------------- */
/* Health                                                                     */
/* -------------------------------------------------------------------------- */

export type Health = {
  status: string;
  environment: string;
  database: string;
  storage: string;
  classifierBackend: string;
  googleOauthConfigured: boolean;
  version: string;
};

/* -------------------------------------------------------------------------- */
/* Small helpers over the above                                               */
/* -------------------------------------------------------------------------- */

const IN_FLIGHT_STATUSES: ReadonlySet<EntryStatus> = new Set<EntryStatus>([
  "pending_upload",
  "queued",
  "developing",
]);

/**
 * True while the entry is still being uploaded or classified — which is exactly
 * when it should be polled. Everything else is terminal.
 */
export function isInFlight(status: EntryStatus): boolean {
  return IN_FLIGHT_STATUSES.has(status);
}
